/* Webinars -> Campaigns: create / edit / activate / pause / close Live or
 * Evergreen campaigns, their sessions, provider + registration method,
 * connected lead sources and TEST/LIVE status. Moved here from
 * Administration -> Webinar. Every write is gated server-side on
 * webinar.config (POST/PATCH /webinar/campaigns, /status, /test-enroll).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, TestBadge } from "@/components/common";
import { Video, Plus, Pencil, AlertTriangle, FlaskConical } from "lucide-react";
import {
  FALLBACK_PROVIDERS, METHOD_LABELS, REGISTRATION_MODE_LABELS, ProviderCapabilityPanel,
  TextPromptDialog, WebinarStatusBadge, CampaignHeadline, toneClass, useWebinarCaps,
  COMMON_TIMEZONES, DEFAULT_TIMEZONE, fmtInTz, utcToLocalInput,
} from "@/components/webinar/webinarShared";
import { WebinarSourcePicker } from "@/components/webinar/WebinarSourceBinding";
import { WebinarReadinessPanel } from "@/components/webinar/WebinarReadinessPanel";

const emptyForm = {
  name: "", description: "", product_code: "", campaign_type: "live",
  provider: "fake", provider_webinar_id: "", registration_mode: "auto",
  provider_registration_method: "api", provider_schedule_id: "", external_registration_url: "",
  scheduled_start: "", duration_minutes: 60, test_mode: true,
  connected_sources: [], timezone: DEFAULT_TIMEZONE, cta_threshold: "50",
  anika_followup_enabled: false, noshow_reinvite_enabled: false,
  evergreen_times: "19:00", nurture_days: "",
};

const formFromCampaign = (c) => ({
  ...emptyForm,
  name: c.name || "", description: c.description || "", product_code: c.product_code || "",
  campaign_type: c.campaign_type || "live", provider: c.provider || "fake",
  provider_webinar_id: c.provider_webinar_id || "", registration_mode: c.registration_mode || "auto",
  provider_registration_method: c.provider_registration_method || c.provider_readiness?.registration_method || "api",
  provider_schedule_id: c.provider_schedule_id || "",
  external_registration_url: c.external_registration_url || "",
  // The start is shown as WALL-CLOCK time in the campaign's own timezone
  // (never the browser's), so what an admin reads is what a customer gets.
  timezone: c.timezone || DEFAULT_TIMEZONE,
  scheduled_start: utcToLocalInput(c.scheduled_start, c.timezone || DEFAULT_TIMEZONE),
  duration_minutes: c.duration_minutes || 60,
  test_mode: c.test_mode !== false,
  connected_sources: (c.connected_sources || []).filter((x) => !x.missing)
    .map((x) => `${x.source_type}:${x.source_id}`),
  evergreen_times: (c.evergreen_rules?.session_times_ist || []).join(", "),
  nurture_days: (c.nurture?.messages || []).map((m) => m.days_before).join(", "),
});

/* Create (campaign == null) or edit an existing campaign. Schedule fields
 * are never editable after creation: webinar.update_campaign materialises
 * sessions additively and never removes the original one, so changing the
 * time would leave the old session in place (and enrollment picks the
 * earliest open session). Setup fields lock once the campaign leaves
 * draft; automation settings live in Automation & Messaging. */
export const CampaignDialog = ({ open, onOpenChange, products, onSaved, providerRows, campaign }) => {
  const editing = !!campaign;
  const locked = editing && campaign.status !== "draft";
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const rows = providerRows && providerRows.length ? providerRows : FALLBACK_PROVIDERS;
  const providerRow = rows.find((r) => r.provider === form.provider) || rows[0];
  const methods = providerRow?.registration_methods || ["api"];
  const method = methods.includes(form.provider_registration_method)
    ? form.provider_registration_method : (providerRow?.default_registration_method || "api");
  const chooseProvider = (p) => {
    const row = rows.find((r) => r.provider === p);
    setForm((f) => ({ ...f, provider: p,
                      provider_registration_method: row?.default_registration_method || "api" }));
  };
  const needsLink = method === "external_link";
  const needsSchedule = method === "api" && ["webinarjam", "everwebinar"].includes(form.provider);
  // Real source IDs ("<source_type>:<source_id>"), validated server-side.
  const sources = () => (form.connected_sources || []).map((k) => {
    const i = k.indexOf(":");
    return { source_type: k.slice(0, i), source_id: k.slice(i + 1) };
  });

  useEffect(() => { if (open) setForm(campaign ? formFromCampaign(campaign) : emptyForm); }, [open, campaign]);

  const setupBody = () => ({
    product_code: form.product_code,
    provider: form.provider,
    provider_webinar_id: form.provider_webinar_id || null,
    provider_registration_method: method,
    provider_schedule_id: needsSchedule ? (form.provider_schedule_id || null) : null,
    external_registration_url: needsLink ? (form.external_registration_url || null) : null,
    registration_mode: form.registration_mode,
    test_mode: !!form.test_mode,
  });

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        const body = { name: form.name, description: form.description || null,
                       connected_sources: sources() };
        if (!locked) Object.assign(body, setupBody());
        await api.patch(`/webinar/campaigns/${campaign.campaign_id}`, body);
        toast.success("Campaign updated");
      } else {
        const body = {
          name: form.name,
          description: form.description || null,
          campaign_type: form.campaign_type,
          duration_minutes: Number(form.duration_minutes) || 60,
          connected_sources: sources(),
          timezone: form.timezone,
          ...setupBody(),
          post_webinar: {
            cta_threshold: Number(form.cta_threshold),
            cta_message_enabled: true,
            cta_task_enabled: true,
            anika_followup_enabled: !!form.anika_followup_enabled,
            anika_followup_hour_ist: 11,
            noshow_reinvite_enabled: !!form.noshow_reinvite_enabled,
            noshow_reinvite_campaign_id: null,
          },
        };
        if (form.campaign_type === "live") {
          // Wall-clock time in the campaign timezone - the server converts it.
          body.scheduled_start_local = form.scheduled_start || null;
          const days = form.nurture_days
            ? form.nurture_days.split(",").map((s) => Number(s.trim())).filter((n) => n >= 2)
            : [];
          body.nurture = { enabled: days.length > 0, messages: days.map((d) => ({ days_before: d })) };
        } else {
          body.evergreen_rules = {
            session_times_ist: form.evergreen_times.split(",").map((s) => s.trim()).filter(Boolean),
            horizon_days: 30,
          };
        }
        await api.post("/webinar/campaigns", body);
        toast.success("Webinar campaign created (draft)");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const lockHint = locked ? "Locked once the campaign has left draft." : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="webinar-campaign-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit campaign — ${campaign.name}` : "New webinar campaign"}</DialogTitle>
        </DialogHeader>
        {editing && (
          <p className="text-[11px] text-muted-foreground rounded-md border p-2" data-testid="webinar-edit-scope-note">
            The schedule can&apos;t be changed after creation (the original session would stay in
            place) — cancel this campaign and create a new one instead.
            {locked ? " Provider, registration and TEST/LIVE settings are locked because the campaign has left draft." : ""}
            {" "}CTA threshold, reminders, nurture, templates, voice follow-up and no-show re-invite
            are edited under Automation &amp; Messaging.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Name</Label>
            <Input data-testid="webinar-form-name" value={form.name}
                   onChange={(e) => set("name", e.target.value)} placeholder="October Student Visa Masterclass" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Product</Label>
            <Select value={form.product_code} onValueChange={(v) => set("product_code", v)} disabled={locked}>
              <SelectTrigger data-testid="webinar-form-product" title={lockHint || undefined}>
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                {(products || []).map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                ))}
                {form.product_code && !(products || []).some((p) => p.code === form.product_code) && (
                  <SelectItem value={form.product_code}>{form.product_code}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.campaign_type} onValueChange={(v) => set("campaign_type", v)} disabled={editing}>
              <SelectTrigger data-testid="webinar-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live (one-off)</SelectItem>
                <SelectItem value="evergreen">Evergreen (repeating)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.campaign_type === "live" ? (
            <>
              <div>
                <Label className="text-xs">Starts (wall-clock time in {form.timezone})</Label>
                <Input type="datetime-local" data-testid="webinar-form-start" disabled={editing}
                       value={form.scheduled_start} onChange={(e) => set("scheduled_start", e.target.value)} />
              </div>
              {!editing && (
                <div>
                  <Label className="text-xs">Nurture days before (max 4, each ≥ 2)</Label>
                  <Input data-testid="webinar-form-nurture" placeholder="5, 4, 3, 2"
                         value={form.nurture_days} onChange={(e) => set("nurture_days", e.target.value)} />
                </div>
              )}
            </>
          ) : (
            <div className="sm:col-span-2">
              <Label className="text-xs">Session times in {form.timezone} (comma separated)</Label>
              <Input data-testid="webinar-form-evergreen-times" value={form.evergreen_times} disabled={editing}
                     onChange={(e) => set("evergreen_times", e.target.value)} placeholder="11:00, 19:00" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Evergreen campaigns have no daily nurture sequence.
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs">Timezone</Label>
            <Select value={form.timezone} onValueChange={(v) => set("timezone", v)} disabled={editing}>
              <SelectTrigger data-testid="webinar-form-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...new Set([form.timezone, ...COMMON_TIMEZONES])].map((z) => (
                  <SelectItem key={z} value={z}>{z}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Start time, D-1 / day-of reminders and every displayed time use this zone.
            </p>
          </div>
          <div>
            <Label className="text-xs">Duration (minutes)</Label>
            <Input type="number" value={form.duration_minutes} disabled={editing}
                   onChange={(e) => set("duration_minutes", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={form.provider} onValueChange={chooseProvider} disabled={locked}>
              <SelectTrigger data-testid="webinar-form-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                {rows.map((r) => (
                  <SelectItem key={r.provider} value={r.provider} data-testid={`webinar-form-provider-${r.provider}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Registration method</Label>
            <Select value={method} onValueChange={(v) => set("provider_registration_method", v)}
                    disabled={locked || methods.length < 2}>
              <SelectTrigger data-testid="webinar-form-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {methods.map((m) => (
                  <SelectItem key={m} value={m}>{METHOD_LABELS[m] || m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <ProviderCapabilityPanel row={providerRow} method={method} />
          </div>
          {needsLink ? (
            <div className="sm:col-span-2">
              <Label className="text-xs">Provider registration page URL (https://…)</Label>
              <Input data-testid="webinar-form-external-url" value={form.external_registration_url} disabled={locked}
                     placeholder="https://event.webinarjam.com/register/…"
                     onChange={(e) => set("external_registration_url", e.target.value)} />
              <p className="text-[11px] text-muted-foreground mt-1">
                Sent to each lead over WhatsApp. A sent link is tracked as &ldquo;link sent&rdquo;, never as
                registered — confirm registrations by importing the provider&apos;s registrant export.
              </p>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Provider webinar ID</Label>
              <Input data-testid="webinar-form-webinar-id" value={form.provider_webinar_id} disabled={locked}
                     onChange={(e) => set("provider_webinar_id", e.target.value)} />
            </div>
          )}
          {needsSchedule && (
            <div>
              <Label className="text-xs">Provider schedule ID</Label>
              <Input data-testid="webinar-form-schedule-id" value={form.provider_schedule_id} disabled={locked}
                     onChange={(e) => set("provider_schedule_id", e.target.value)} />
            </div>
          )}
          <div>
            <Label className="text-xs">Registration</Label>
            <Select value={form.registration_mode} onValueChange={(v) => set("registration_mode", v)} disabled={locked}>
              <SelectTrigger data-testid="webinar-form-registration-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REGISTRATION_MODE_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!editing && (
            <div>
              <Label className="text-xs">CTA threshold</Label>
              <Select value={form.cta_threshold} onValueChange={(v) => set("cta_threshold", v)}>
                <SelectTrigger data-testid="webinar-form-threshold"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">Attended 50% or more</SelectItem>
                  <SelectItem value="75">Attended 75% or more</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-xs">Connected lead sources</Label>
            <WebinarSourcePicker value={form.connected_sources}
                                 campaignId={campaign?.campaign_id}
                                 onChange={(v) => set("connected_sources", v)} />
            <p className="text-[11px] text-muted-foreground mt-1">
              Pick real sources (Meta lead forms, Google Sheets intake, inbound webhooks). A source can
              feed only one live campaign. Leads from an attached source are enrolled while this
              campaign is active. You can also mark a source as a Webinar source from its own
              configuration page.
            </p>
          </div>
          {!editing && (
            <>
              <label className="flex items-center gap-2 text-xs">
                <Switch checked={!!form.anika_followup_enabled}
                        onCheckedChange={(v) => set("anika_followup_enabled", v)} />
                Next-day Anika voice follow-up
              </label>
              <label className="flex items-center gap-2 text-xs">
                <Switch checked={!!form.noshow_reinvite_enabled}
                        onCheckedChange={(v) => set("noshow_reinvite_enabled", v)} />
                Re-invite no-shows
              </label>
            </>
          )}
          <label className="sm:col-span-2 flex items-center gap-2 text-xs rounded-md border p-2">
            <Switch data-testid="webinar-form-testmode" checked={!!form.test_mode} disabled={locked}
                    onCheckedChange={(v) => set("test_mode", v)} />
            <span>
              <span className="font-medium">TEST MODE</span> — API registration uses the simulated
              provider and every record and message is marked TEST, so nothing real is ever
              dispatched. With TEST MODE off, an unusable provider blocks activation — it is never
              silently replaced by the simulated one.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="webinar-form-submit"
                  disabled={busy || !form.name || !form.product_code
                            || (!locked && needsLink && !form.external_registration_url.startsWith("https://"))}
                  onClick={submit}>{editing ? "Save changes" : "Create draft"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Test-enroll a TEST lead through the real intake path (TEST MODE
 * campaigns only). Nothing is dispatched: the lead is flagged
 * is_test_record and the outbox refuses to send TEST records. */
const TestEnrollDialog = ({ campaign, open, onOpenChange, onDone }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "TEST Webinar Lead", phone: "", email: "" }); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/webinar/campaigns/${campaign.campaign_id}/test-enroll`,
                               { name: form.name, phone: form.phone, email: form.email || null });
      const skipped = r.data?.webinar_result?.skipped;
      if (skipped) toast.warning(`Lead created, but not enrolled: ${skipped.replace(/_/g, " ")}`);
      else toast.success(`TEST lead enrolled (${(r.data?.enrollment?.registration_state || "").replace(/_/g, " ")})`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="webinar-test-enroll-dialog">
        <DialogHeader><DialogTitle>Test enroll — {campaign?.name}</DialogTitle></DialogHeader>
        <p className="text-[11px] text-muted-foreground">
          Runs the real lead-intake + enrollment path with a TEST-flagged lead. Enrollment only
          happens while the webinar master switch is ON and the campaign is active.
        </p>
        <div className="space-y-2">
          <div><Label className="text-xs">Name</Label>
            <Input value={form.name} data-testid="webinar-test-enroll-name"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label className="text-xs">Phone</Label>
            <Input value={form.phone} data-testid="webinar-test-enroll-phone"
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label className="text-xs">Email (optional)</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button disabled={busy || !form.name || !form.phone} onClick={submit}
                  data-testid="webinar-test-enroll-submit">Enroll TEST lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CampaignDetail = ({ campaign, onOpenCampaign }) => {
  const [sessions, setSessions] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/webinar/campaigns/${campaign.campaign_id}/sessions`)
      .then((r) => { if (alive) setSessions(r.data.items); })
      .catch((e) => toast.error(errDetail(e)));
    return () => { alive = false; };
  }, [campaign.campaign_id]);
  const readiness = campaign.provider_readiness;
  const now = new Date();
  const upcoming = (sessions || []).filter((s) => new Date(s.scheduled_start) > now);
  const past = (sessions || []).filter((s) => new Date(s.scheduled_start) <= now);
  return (
    <Card data-testid="webinar-campaign-detail">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading"><CampaignHeadline campaign={campaign} /></CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {readiness && !readiness.ready && (
          <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-2 flex items-start gap-2"
               data-testid="webinar-provider-blocked">
            <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--danger))]" />
            <span><span className="font-medium">Provider blocked:</span> {readiness.blocked_reason}</span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div><p className="text-muted-foreground text-[11px]">Product</p><p>{campaign.product_code}</p></div>
          <div><p className="text-muted-foreground text-[11px]">Provider</p>
            <p data-testid="webinar-provider-method">
              {campaign.provider} · {METHOD_LABELS[readiness?.registration_method] || readiness?.registration_method}
              {readiness?.uses_simulated_provider ? " · simulated in TEST MODE" : ""}
            </p></div>
          <div><p className="text-muted-foreground text-[11px]">Registration</p>
            <p>{REGISTRATION_MODE_LABELS[campaign.registration_mode] || campaign.registration_mode}</p></div>
          <div><p className="text-muted-foreground text-[11px]">Connected lead sources</p>
            <p className="break-words" data-testid="webinar-detail-sources">
              {(campaign.connected_sources || []).map((x) => x.label).join("; ")
                || "None — attach a lead source (or mark one as a Webinar source in its configuration)"}
            </p></div>
          <div><p className="text-muted-foreground text-[11px]">Timezone</p>
            <p className="font-mono" data-testid="webinar-detail-timezone">{campaign.timezone || DEFAULT_TIMEZONE}</p></div>
        </div>
        <div>
          <p className="font-medium mb-1">Sessions ({sessions?.length ?? "…"})</p>
          {sessions && sessions.length === 0 ? (
            <p className="text-muted-foreground">
              No sessions — {campaign.campaign_type === "live" ? "set a start time when creating the campaign" : "no evergreen session times configured"}.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-64">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Starts ({campaign.timezone || DEFAULT_TIMEZONE})</TableHead><TableHead>Status</TableHead>
                    <TableHead className="text-right">Enrolled</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {[...upcoming.slice(0, 10), ...past.slice(-5).reverse()].map((s) => (
                    <TableRow key={s.session_id}>
                      <TableCell className="text-xs">{fmtInTz(s.scheduled_start, campaign.timezone)}</TableCell>
                      <TableCell className="text-xs capitalize">{s.status}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.enrolled_total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {upcoming.length > 10 && (
                <p className="text-[11px] text-muted-foreground mt-1">+{upcoming.length - 10} more upcoming sessions.</p>
              )}
            </div>
          )}
        </div>
        <WebinarReadinessPanel campaignId={campaign.campaign_id}
                               refreshKey={`${campaign.status}:${campaign.updated_at || ""}`} />
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onOpenCampaign(campaign.campaign_id, "registrations")}>
            Registrations &amp; attendance
          </Button>
          <Button size="sm" variant="outline" onClick={() => onOpenCampaign(campaign.campaign_id, "automation")}>
            Automation &amp; messaging
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const WebinarCampaigns = ({ campaigns, campaignsTotal, settings, products, onChanged,
                                   selectedId, onSelect, onOpenCampaign }) => {
  const { canConfig } = useWebinarCaps();
  const [dialog, setDialog] = useState(null); // {campaign|null}
  const [testEnroll, setTestEnroll] = useState(null);
  const [closePrompt, setClosePrompt] = useState(null); // {campaign, to}
  const [statusFilter, setStatusFilter] = useState("open");

  const changeStatus = useCallback(async (campaignId, status, reason) => {
    try {
      await api.post(`/webinar/campaigns/${campaignId}/status`, { status, reason: reason || null });
      toast.success(`Campaign ${status}`);
      onChanged();
    } catch (e) { toast.error(errDetail(e)); throw e; }
  }, [onChanged]);

  const rows = (campaigns || []).filter((c) => statusFilter === "all"
    || (statusFilter === "open" ? ["draft", "active", "paused"].includes(c.status) : c.status === statusFilter));
  const selected = (campaigns || []).find((c) => c.campaign_id === selectedId);

  return (
    <div className="space-y-4" data-testid="webinar-campaigns">
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Video className="h-4 w-4" /> Campaigns ({campaignsTotal ?? 0})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[170px]" data-testid="webinar-campaign-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Draft, active &amp; paused</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            {canConfig && (
              <Button size="sm" data-testid="webinar-new-campaign" onClick={() => setDialog({ campaign: null })}>
                <Plus className="h-3.5 w-3.5 mr-1" /> New campaign
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!campaigns ? null : rows.length === 0 ? (
            <EmptyState icon={Video} title={campaigns.length ? "No campaigns match this filter" : "No webinar campaigns yet"}
                        hint={canConfig ? "Create one to start enrolling leads from a connected source." : "Nothing to show yet."}
                        testId="webinar-empty" />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Type</TableHead>
                    <TableHead>Product</TableHead><TableHead>Provider</TableHead>
                    <TableHead>Status</TableHead><TableHead className="text-right">Enrolled</TableHead>
                    <TableHead>Starts / next</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.campaign_id} className={`cursor-pointer ${c.campaign_id === selectedId ? "bg-muted/50" : ""}`}
                              data-testid={`webinar-campaign-row-${c.campaign_id}`}
                              onClick={() => onSelect(c.campaign_id)}>
                      <TableCell className="text-xs font-medium">
                        {c.name} <TestBadge show={c.test_mode} />
                        {!c.test_mode && (
                          <Badge variant="outline" className={`ml-1 text-[10px] ${toneClass("danger")}`}>LIVE</Badge>
                        )}
                        {c.provider_readiness && !c.provider_readiness.ready && (
                          <Badge variant="outline" data-testid={`webinar-blocked-${c.campaign_id}`}
                                 title={c.provider_readiness.blocked_reason}
                                 className={`ml-1 text-[10px] ${toneClass("danger")}`}>
                            provider blocked
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs capitalize">{c.campaign_type}</TableCell>
                      <TableCell className="text-xs">{c.product_code}</TableCell>
                      <TableCell className="text-xs">
                        {c.provider}
                        <span className="block text-[10px] text-muted-foreground">
                          {c.provider_readiness?.registration_method === "external_link" ? "shared link" : "API"}
                        </span>
                      </TableCell>
                      <TableCell><WebinarStatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{c.enrolled_total}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {c.scheduled_start ? fmtInTz(c.scheduled_start, c.timezone)
                          : c.campaign_type === "evergreen"
                            ? `Daily ${(c.evergreen_rules?.session_times_ist || []).join(", ")} ${c.timezone || DEFAULT_TIMEZONE}` : "—"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {canConfig && ["draft", "active", "paused"].includes(c.status) && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" title="Edit"
                                  data-testid={`webinar-edit-${c.campaign_id}`}
                                  onClick={() => setDialog({ campaign: c })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canConfig && c.status === "draft" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs ml-1" data-testid={`webinar-activate-${c.campaign_id}`}
                                  disabled={c.provider_readiness && !c.provider_readiness.ready}
                                  title={c.provider_readiness?.blocked_reason || undefined}
                                  onClick={() => changeStatus(c.campaign_id, "active").catch(() => {})}>Activate</Button>
                        )}
                        {canConfig && c.status === "active" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs ml-1" data-testid={`webinar-pause-${c.campaign_id}`}
                                  onClick={() => changeStatus(c.campaign_id, "paused").catch(() => {})}>Pause</Button>
                        )}
                        {canConfig && c.status === "paused" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs ml-1" data-testid={`webinar-resume-${c.campaign_id}`}
                                  onClick={() => changeStatus(c.campaign_id, "active").catch(() => {})}>Resume</Button>
                        )}
                        {canConfig && ["active", "paused"].includes(c.status) && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs ml-1" data-testid={`webinar-complete-${c.campaign_id}`}
                                  onClick={() => setClosePrompt({ campaign: c, to: "completed" })}>Close</Button>
                        )}
                        {canConfig && ["draft", "active", "paused"].includes(c.status) && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs ml-1 text-[hsl(var(--danger))]"
                                  data-testid={`webinar-cancel-campaign-${c.campaign_id}`}
                                  onClick={() => setClosePrompt({ campaign: c, to: "cancelled" })}>Cancel</Button>
                        )}
                        {canConfig && c.test_mode && c.status === "active" && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 ml-1" title="Test enroll a TEST lead"
                                  data-testid={`webinar-test-enroll-${c.campaign_id}`}
                                  onClick={() => setTestEnroll(c)}>
                            <FlaskConical className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && <CampaignDetail key={selected.campaign_id} campaign={selected} onOpenCampaign={onOpenCampaign} />}

      <CampaignDialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}
                      campaign={dialog?.campaign || null} products={products}
                      providerRows={settings?.provider_capabilities} onSaved={onChanged} />
      <TestEnrollDialog campaign={testEnroll} open={!!testEnroll}
                        onOpenChange={(o) => { if (!o) setTestEnroll(null); }} onDone={onChanged} />
      <TextPromptDialog
        open={!!closePrompt}
        onOpenChange={(o) => { if (!o) setClosePrompt(null); }}
        testId="webinar-close-prompt"
        title={closePrompt?.to === "completed" ? `Close campaign — ${closePrompt?.campaign?.name}`
          : `Cancel campaign — ${closePrompt?.campaign?.name}`}
        label={closePrompt?.to === "completed"
          ? "Reason (the campaign stops enrolling and cannot be reopened)"
          : "Reason (cancelling is permanent — no further enrollments, reminders or follow-ups)"}
        onSubmit={(reason) => changeStatus(closePrompt.campaign.campaign_id, closePrompt.to, reason)}
      />
    </div>
  );
};

export default WebinarCampaigns;
